import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  DashboardType,
  CSVUploadStatus,
  FileImportType,
  ReplenishType,
  OrderSearchCriteriaEnum,
  ReplenishSearchCriteriaEnum,
  StockTransferSearchCriteriaEnum,
  InventoryAdjustmentSearchCriteriaEnum,
  DeliveryNoteSearchCriteriaEnum,
  DeliveryNoteStatusEnum,
  BinNetsuiteSyncStatusEnum,
} from 'commons/enums';
import {
  PRIMARY_BLUE,
  PRIMARY_GREEN,
  PRIMARY_RED,
} from 'commons/styles/colors';
import {
  DashboardActiveOrderSummaries,
  DashboardShiftSummaries,
  IInboundInvoiceIdentifier,
  IInboundTaskItem,
  ProductDetail,
} from 'commons/types';
import { defaultActionProcess } from 'commons/utils/defaultActionProcess';
import {
  setActiveLocations,
  setSelectedLocation,
  setAllOrderLists,
  setAllReplenishLists,
  setAllStockTransferLists,
  setAllUserLists,
  setHighPriorityValue,
  setBulkUploadHistory,
  setSummariesActiveOrder,
  setSummariesShifts,
  setUpdateListPage,
  setHighPriorityValueReplenish,
  setAllBinLists,
  addBinToList,
  setAllBinSkuLists,
  setBinCommodityList,
  setEnabledBinCommodityList,
  setAllInventoryAdjustmentLists,
  setFailedOrders,
  removeOrderFromList,
  setDeliveryNoteStatus,
  setAllSTODocumentList,
  setAllSTOItemList,
  setBinType,
  setBinCommodity,
  setAllInboundTaskDocumentList,
  setAllInboundTaskItemList,
  setAllInboundInvoiceList,
  setAllInboundInvoiceItemList,
  setAllDeliveryNoteLists,
} from 'redux-stores/reducers/dashboard.reducer';
import { snackbarSetData } from 'redux-stores/reducers/utilityReducer';
import { AppDispatch } from 'redux-stores/store';
import { swiperxWmsApiDashboard } from 'services/apis/SwipeRxWmsApiDashboard';
import {
  IBinProductDetailRequest,
  IBinProductDetailResponse,
} from 'services/apis/SwipeRxWmsApiDashboard/types';
import {
  PackingActiveOrderSummariesResponse,
  PackingShiftSummariesResponse,
  PickingActiveOrderSummariesResponse,
  PickingShiftSummariesResponse,
} from 'services/apis/SwipeRxWmsApiDashboard/types/get-summaries.type';
import { InboundTaskItemAssignUnassignRequest } from 'services/apis/SwipeRxWmsApiInboundTaskItem/type';

const defaultSearchParams = new Set(['page', 'page_size']);

export const getOrderListAction = createAsyncThunk(
  'dashboard/getOrderListAction',
  async (
    params: { searchParams?: URLSearchParams; locationId?: string },
    { dispatch },
  ) => {
    const { searchParams, locationId } = params;

    const hasOtherParams =
      searchParams &&
      Array.from(searchParams.keys()).some(
        (key) => !defaultSearchParams.has(key),
      );

    if (hasOtherParams) {
      const queries: Record<string, string | string> = {};
      searchParams?.forEach((value, key) => {
        if (key === 'status' && value === OrderSearchCriteriaEnum.CANCELLED) {
          queries.in_status = `${OrderSearchCriteriaEnum.CANCELLED},${OrderSearchCriteriaEnum.NON_PICKED}`;
        } else if (key !== 'status' || value !== OrderSearchCriteriaEnum.ALL)
          queries[key] = value;
      });

      if (locationId) queries.location = locationId;

      defaultActionProcess(async () => {
        const data =
          await swiperxWmsApiDashboard.getOrderListPaginated(queries);
        dispatch(setAllOrderLists(data));
      }, dispatch);
    }
  },
);

export const getReplenishListAction = createAsyncThunk(
  'dashboard/getReplenishListAction',
  async (
    params: { searchParams?: URLSearchParams; locationId?: string },
    { dispatch },
  ) => {
    const { searchParams, locationId } = params;

    const hasOtherParams =
      searchParams &&
      Array.from(searchParams.keys()).some(
        (key) => !defaultSearchParams.has(key),
      );

    if (hasOtherParams) {
      const queries: Record<string, string | string> = {};
      searchParams?.forEach((value, key) => {
        if (key !== 'status' || value !== ReplenishSearchCriteriaEnum.ALL)
          queries[key] = value;
      });

      if (locationId) queries.location = locationId;

      defaultActionProcess(async () => {
        const data =
          await swiperxWmsApiDashboard.getReplenishListPaginated(queries);
        dispatch(setAllReplenishLists(data));
      }, dispatch);
    }
  },
);

export const getUserListAction = createAsyncThunk(
  'dashboard/getUserListAction',
  async (
    params: { searchParams?: URLSearchParams; locationId?: string },
    { dispatch },
  ) => {
    const { searchParams, locationId } = params;
    const queries: Record<string, string | string> = {};
    searchParams?.forEach((value, key) => {
      queries[key] = value;
    });

    if (locationId) queries.location = locationId;

    defaultActionProcess(async () => {
      const data = await swiperxWmsApiDashboard.getUserListPaginated(queries);
      dispatch(setAllUserLists(data));
    }, dispatch);
  },
);

export const getBinListAction = createAsyncThunk(
  'dashboard/getBinListAction',
  async (
    params: { searchParams?: URLSearchParams; locationId?: string },
    { dispatch },
  ) => {
    const { searchParams, locationId } = params;
    const queries: Record<string, string | string> = {};
    searchParams?.forEach((value, key) => {
      queries[key] = value;
    });

    if (locationId) queries.location = locationId;

    defaultActionProcess(async () => {
      const data = await swiperxWmsApiDashboard.getBinListPaginated(queries);
      dispatch(setAllBinLists(data));
    }, dispatch);
  },
);

export const getBinListWithSkuAction = createAsyncThunk(
  'dashboard/getBinListWithSkuAction',
  async (
    params: { searchParams?: URLSearchParams; locationId?: string },
    { dispatch },
  ) => {
    const { searchParams, locationId } = params;
    const queries: Record<string, string | string> = {};
    searchParams?.forEach((value, key) => {
      if (key === 'bin_view') return;
      queries[key] = value;
    });

    if (locationId) queries.location = locationId;

    defaultActionProcess(async () => {
      const data =
        await swiperxWmsApiDashboard.getBinListWithSkuPaginated(queries);
      dispatch(setAllBinSkuLists(data));
    }, dispatch);
  },
);

export const getBinCommoditiesAction = createAsyncThunk(
  'dashboard/getBinCommoditiesAction',
  async (params: { isEnabled?: boolean } | undefined, { dispatch }) => {
    defaultActionProcess(async () => {
      if (params?.isEnabled) {
        const data = await swiperxWmsApiDashboard.getBinCommodities({
          is_enabled: true,
        });
        dispatch(setEnabledBinCommodityList(data));
        return;
      }
      const data = await swiperxWmsApiDashboard.getBinCommodities();
      dispatch(setBinCommodityList(data));
    }, dispatch);
  },
);

export const createBinCommodityAction = createAsyncThunk(
  'dashboard/createBinCommodityAction',
  async (name: string, { dispatch }) => {
    defaultActionProcess(async () => {
      await swiperxWmsApiDashboard.createBinCommodity(name);
      dispatch(
        snackbarSetData({
          open: true,
          message: `Successfully created commodity ${name}`,
          color: PRIMARY_GREEN,
        }),
      );
      dispatch(getBinCommoditiesAction());
    }, dispatch);
  },
);

export const updateBinCommodityNameAction = createAsyncThunk(
  'dashboard/updateBinCommodityNameAction',
  async (params: { id: string; name: string }, { dispatch }) => {
    const { id, name } = params;
    defaultActionProcess(async () => {
      await swiperxWmsApiDashboard.updateBinCommodityName(id, name);
      dispatch(
        snackbarSetData({
          open: true,
          message: `Successfully updated commodity to ${name}`,
          color: PRIMARY_GREEN,
        }),
      );
      dispatch(getBinCommoditiesAction());
    }, dispatch);
  },
);

export const updateBinCommodityStatusAction = createAsyncThunk(
  'dashboard/updateBinCommodityStatusAction',
  async (
    params: { id: string; name: string; isEnabled: boolean },
    { dispatch },
  ) => {
    const { id, name, isEnabled } = params;
    defaultActionProcess(async () => {
      await swiperxWmsApiDashboard.updateBinCommodityStatus(id, isEnabled);
      dispatch(
        snackbarSetData({
          open: true,
          message: `Successfully ${
            isEnabled ? 'enabled' : 'disabled'
          } commodity ${name}`,
          color: PRIMARY_GREEN,
        }),
      );
      dispatch(getBinCommoditiesAction());
    }, dispatch);
  },
);

export const deleteBinCommodityAction = createAsyncThunk(
  'dashboard/deleteBinCommodityAction',
  async (params: { id: string; name: string }, { dispatch }) => {
    const { id, name } = params;
    defaultActionProcess(async () => {
      await swiperxWmsApiDashboard.deleteBinCommodity(id);
      dispatch(
        snackbarSetData({
          open: true,
          message: `Successfully deleted commodity ${name}`,
          color: PRIMARY_GREEN,
        }),
      );
      dispatch(getBinCommoditiesAction());
    }, dispatch);
  },
);

export const getStockTransferListAction = createAsyncThunk(
  'dashboard/getStockTransferListAction',
  async (
    params: { searchParams?: URLSearchParams; locationId?: string },
    { dispatch },
  ) => {
    const { searchParams, locationId } = params;

    const hasOtherParams =
      searchParams &&
      Array.from(searchParams.keys()).some(
        (key) => !defaultSearchParams.has(key),
      );

    if (hasOtherParams) {
      const queries: Record<string, string> = {};
      searchParams?.forEach((value, key) => {
        if (key !== 'status' || value !== StockTransferSearchCriteriaEnum.ALL)
          queries[key] = value;
      });

      if (locationId) queries.location = locationId;

      defaultActionProcess(async () => {
        const data =
          await swiperxWmsApiDashboard.getStockTransferListPaginated(queries);
        dispatch(setAllStockTransferLists(data));
      }, dispatch);
    }
  },
);

export const getSTODocumentListAction = createAsyncThunk(
  'dashboard/getSTODocumentListAction',
  async (
    params: { searchParams?: URLSearchParams; locationId?: string },
    { dispatch },
  ) => {
    const { searchParams, locationId } = params;

    const hasOtherParams =
      searchParams &&
      Array.from(searchParams.keys()).some(
        (key) => !defaultSearchParams.has(key),
      );
    if (hasOtherParams) {
      const queries: Record<string, string | string> = {};
      searchParams?.forEach((value, key) => {
        if (key !== 'status' || value !== 'all') queries[key] = value;
      });

      if (locationId) queries.location = locationId;

      defaultActionProcess(async () => {
        const data =
          await swiperxWmsApiDashboard.getSTODocumentListPaginated(queries);
        dispatch(setAllSTODocumentList(data));
      }, dispatch);
    }
  },
);

export const getSTOItemListAction = createAsyncThunk(
  'dashboard/getSTOItemListAction',
  async (stoNumber: string, { dispatch }) => {
    defaultActionProcess(
      async () => {
        const data = await swiperxWmsApiDashboard.getSTOItemList(stoNumber);
        dispatch(setAllSTOItemList({ sto_number: stoNumber, list: data }));
      },
      dispatch,
      false,
    );
  },
);

export const getInventoryAdjustmentListAction = createAsyncThunk(
  'dashboard/getInventoryAdjustmentListAction',
  async (
    params: { searchParams?: URLSearchParams; locationId?: string },
    { dispatch },
  ) => {
    const { searchParams, locationId } = params;

    const hasOtherParams =
      searchParams &&
      Array.from(searchParams.keys()).some(
        (key) => !defaultSearchParams.has(key),
      );

    if (hasOtherParams) {
      const queries: Record<string, string> = {};
      searchParams?.forEach((value, key) => {
        if (
          key !== 'status' ||
          value !== InventoryAdjustmentSearchCriteriaEnum.ALL
        )
          queries[key] = value;
      });

      if (locationId) queries.location = locationId;

      defaultActionProcess(async () => {
        const data =
          await swiperxWmsApiDashboard.getInventoryAdjustmentListPaginated(
            queries,
          );
        dispatch(setAllInventoryAdjustmentLists(data));
      }, dispatch);
    }
  },
);

export const getInboundTaskDocumentListAction = createAsyncThunk(
  'dashboard/getInboundTaskDocumentListAction',
  async (
    params: { searchParams?: URLSearchParams; locationId?: string },
    { dispatch },
  ) => {
    const { searchParams, locationId } = params;

    const hasOtherParams =
      searchParams &&
      Array.from(searchParams.keys()).some(
        (key) => !defaultSearchParams.has(key),
      );
    if (hasOtherParams) {
      const queries: Record<string, string | string> = {};
      searchParams?.forEach((value, key) => {
        if (key !== 'status' || value !== 'all') queries[key] = value;
      });

      if (locationId) queries.location = locationId;

      defaultActionProcess(async () => {
        const data =
          await swiperxWmsApiDashboard.getInboundTaskDocumentListPaginated(
            queries,
          );
        dispatch(setAllInboundTaskDocumentList(data));
      }, dispatch);
    }
  },
);

export const getInboundTaskItemListAction = createAsyncThunk(
  'dashboard/getInboundTaskItemListAction',
  async (inboundTaskNumber: string, { dispatch }) => {
    defaultActionProcess(
      async () => {
        const data =
          await swiperxWmsApiDashboard.getInboundTaskItemList(
            inboundTaskNumber,
          );
        dispatch(
          setAllInboundTaskItemList({
            inbound_task_number: inboundTaskNumber,
            list: [...data].sort((a, b) => {
              const skuCompare = a.sku_code.localeCompare(b.sku_code);
              if (skuCompare !== 0) return skuCompare;
              return a.is_main_item === b.is_main_item
                ? 0
                : a.is_main_item
                ? -1
                : 1;
            }),
          }),
        );
      },
      dispatch,
      false,
    );
  },
);

export const getInboundInvoiceListAction = createAsyncThunk(
  'dashboard/getInboundInvoiceListAction',
  async (
    params: { searchParams?: URLSearchParams; locationId?: string },
    { dispatch },
  ) => {
    const { searchParams, locationId } = params;

    const hasOtherParams =
      searchParams &&
      Array.from(searchParams.keys()).some(
        (key) => !defaultSearchParams.has(key),
      );
    if (hasOtherParams) {
      const queries: Record<string, string | string> = {};
      searchParams?.forEach((value, key) => {
        if (key !== 'integration_status' || value !== 'all')
          queries[key] = value;
      });

      if (locationId) queries.location = locationId;

      defaultActionProcess(async () => {
        const data =
          await swiperxWmsApiDashboard.getInboundInvoiceListPaginated(queries);
        dispatch(setAllInboundInvoiceList(data));
      }, dispatch);
    }
  },
);

export const getInboundInvoiceItemListAction = createAsyncThunk(
  'dashboard/getInboundInvoiceItemListAction',
  async (
    params: { inboundTaskNumber: string; invoiceNumber: string },
    { dispatch },
  ) => {
    const { inboundTaskNumber, invoiceNumber } = params;
    defaultActionProcess(
      async () => {
        const data = await swiperxWmsApiDashboard.getInboundInvoiceItemList(
          inboundTaskNumber,
          invoiceNumber,
        );
        dispatch(
          setAllInboundInvoiceItemList({
            inbound_task_number: inboundTaskNumber,
            invoice_number: invoiceNumber,
            list: data,
          }),
        );
      },
      dispatch,
      false,
    );
  },
);

export const retryUpdateFailedInboundInvoice = createAsyncThunk(
  'dashboard/retryUpdateFailedInboundInvoice',
  async (
    payload: {
      invoiceNumber: string;
      inboundTaskNumber: string;
      location?: number;
    },
    { dispatch },
  ) => {
    const { invoiceNumber, inboundTaskNumber, location } = payload;
    defaultActionProcess(async () => {
      const success = await swiperxWmsApiDashboard.retryUpdateInboundInvoice(
        invoiceNumber,
        inboundTaskNumber,
        location,
      );

      if (success) {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Submit retry Inbound Invoice ${invoiceNumber} for PO ${inboundTaskNumber} done!`,
            color: PRIMARY_GREEN,
          }),
        );
      }
    }, dispatch);
  },
);

export const bulkRetryUpdateFailedInboundInvoice = createAsyncThunk(
  'dashboard/bulkRetryUpdateFailedInboundInvoice',
  async (
    payload: {
      invoiceIdentifiers: IInboundInvoiceIdentifier[];
      location?: number;
    },
    { dispatch },
  ) => {
    defaultActionProcess(async () => {
      const { invoiceIdentifiers, location } = payload;
      const result = await swiperxWmsApiDashboard.bulkRetryUpdateInboundInvoice(
        invoiceIdentifiers,
        location,
      );

      if (result.skipped.length === 0) {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Submit Retry Inbound Invoice done!
            \n ${result.success
              .map(
                (invoiceIdentifier) =>
                  `${invoiceIdentifier.invoice_number} (PO number: ${invoiceIdentifier.inbound_task_number})`,
              )
              .join('\n')}`,
            color: PRIMARY_GREEN,
          }),
        );
      } else {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Submit Retry Inbound Invoice done: \n ${result.success
              .map(
                (invoiceIdentifier) =>
                  `${invoiceIdentifier.invoice_number} (PO number: ${invoiceIdentifier.inbound_task_number})`,
              )
              .join('\n')} \n while Inbound Invoice skipped:
            \n ${result.skipped
              .map(
                (invoiceIdentifier) =>
                  `${invoiceIdentifier.invoice_number} (PO number: ${invoiceIdentifier.inbound_task_number})`,
              )
              .join('\n')}`,
            color: PRIMARY_BLUE,
          }),
        );
      }
    }, dispatch);
  },
);

export const resetInboundTaskItemAssignment = createAsyncThunk(
  'dashboard/resetInboundTaskItemAssignment',
  async (item: IInboundTaskItem, { dispatch }) => {
    await defaultActionProcess(async () => {
      const payload: InboundTaskItemAssignUnassignRequest = {
        inbound_task_number: item.inbound_task_number,
        sku_code: item.sku_code,
        foc: item.foc,
      };
      await swiperxWmsApiDashboard.resetInboundTaskItemAssignment(payload);
    }, dispatch);
  },
);

export const getActiveOrderSummaries = createAsyncThunk(
  'dashboard/getActiveOrderSummaries',
  async (
    params: { location: number | undefined; type: DashboardType },
    { dispatch },
  ) => {
    const { location, type } = params;
    defaultActionProcess(async () => {
      let data: DashboardActiveOrderSummaries | null;
      let rawData:
        | PickingActiveOrderSummariesResponse
        | PackingActiveOrderSummariesResponse
        | null;
      switch (type) {
        case DashboardType.PICKING:
          rawData =
            await swiperxWmsApiDashboard.getPickingSummariesActiveOrders(
              location,
            );
          data = {
            currentActiveUserCount: rawData?.currentActivePickerCount || 0,
            orderCounts: rawData?.orderCounts || {
              alkes: { in_progress: 0, in_queue: 0 },
              precursor: { in_progress: 0, in_queue: 0 },
              regular: { in_progress: 0, in_queue: 0 },
              ccp: { in_progress: 0, in_queue: 0 },
              psikotropika: { in_progress: 0, in_queue: 0 },
              oot: { in_progress: 0, in_queue: 0 },
            },
          };
          break;
        case DashboardType.PACKING:
          rawData =
            await swiperxWmsApiDashboard.getPackingSummariesActiveOrders(
              location,
            );
          data = {
            currentActiveUserCount: rawData?.currentActivePackerCount || 0,
            orderCounts: rawData?.orderCounts || {
              alkes: { in_progress: 0, in_queue: 0 },
              precursor: { in_progress: 0, in_queue: 0 },
              regular: { in_progress: 0, in_queue: 0 },
              ccp: { in_progress: 0, in_queue: 0 },
              psikotropika: { in_progress: 0, in_queue: 0 },
              oot: { in_progress: 0, in_queue: 0 },
            },
          };
          break;
        default:
          data = null;
          break;
      }
      // await swiperxWmsApiDashboard.getPickingSummariesActiveOrders(location);
      if (data) {
        dispatch(setSummariesActiveOrder(data));
      }
    }, dispatch);
  },
);

export const getShiftSummaries = createAsyncThunk(
  'dashboard/getShiftSummaries',
  async (
    params: { location: number | undefined; type: DashboardType },
    { dispatch },
  ) => {
    const { location, type } = params;
    defaultActionProcess(async () => {
      let data: DashboardShiftSummaries | null;
      let rawData:
        | PickingShiftSummariesResponse
        | PackingShiftSummariesResponse
        | null;
      switch (type) {
        case DashboardType.PICKING:
          rawData =
            await swiperxWmsApiDashboard.getPickingSummariesShift(location);
          data = {
            orderPerShifts: (rawData?.orderPickedPerShifts || []).map(
              (item) => ({
                order_count: item.picked,
                shift: item.shift,
              }),
            ),
            topUsers: (rawData?.topPickers || []).map((item) => ({
              user: item.picker,
              count: item.picked,
            })),
          };
          break;
        case DashboardType.PACKING:
          rawData =
            await swiperxWmsApiDashboard.getPackingSummariesShift(location);
          data = {
            orderPerShifts: (rawData?.orderPackedPerShifts || []).map(
              (item) => ({
                order_count: item.packed,
                shift: item.shift,
              }),
            ),
            topUsers: (rawData?.topPackers || []).map((item) => ({
              user: item.packer,
              count: item.packed,
            })),
          };
          break;
        default:
          data = null;
          break;
      }
      if (data) {
        dispatch(setSummariesShifts(data));
      }
    }, dispatch);
  },
);

export const getFailedOrders = createAsyncThunk(
  'dashboard/getFailedOrders',
  async (payload: { location?: number }, { dispatch }) => {
    defaultActionProcess(async () => {
      const { location } = payload;
      const result = await swiperxWmsApiDashboard.getFailedOrders(location);

      dispatch(setFailedOrders({ failedOrders: result }));
    }, dispatch);
  },
);

export const prioritizeOrderPick = createAsyncThunk(
  'dashboard/prioritizeOrderPick',
  async (payload: { poNumber: string; location?: number }, { dispatch }) => {
    defaultActionProcess(async () => {
      const { poNumber, location } = payload;
      const success = await swiperxWmsApiDashboard.prioritizeOrderPick(
        poNumber,
        location,
      );

      if (success) {
        dispatch(
          setHighPriorityValue({
            po_numbers: [poNumber],
            value: true,
          }),
        );
        dispatch(
          snackbarSetData({
            open: true,
            message: `Prioritize PO ${poNumber} success!`,
            color: PRIMARY_GREEN,
          }),
        );
      } else {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Prioritize PO ${poNumber} cannot be prioritized`,
            color: PRIMARY_BLUE,
          }),
        );
      }
    }, dispatch);
  },
);

export const bulkPrioritizeOrderPick = createAsyncThunk(
  'dashboard/bulkPrioritizeOrderPick',
  async (payload: { poNumbers: string[]; location?: number }, { dispatch }) => {
    defaultActionProcess(async () => {
      const { poNumbers, location } = payload;
      const result = await swiperxWmsApiDashboard.bulkPrioritizeOrderPick(
        poNumbers,
        location,
      );

      if (result.skipped.length === 0) {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Prioritize PO success! \n ${result.success.join('\n')}`,
            color: PRIMARY_GREEN,
          }),
        );
      } else {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Prioritize PO success: \n ${result.success.join(
              '\n',
            )} \n while PO skipped: \n ${result.skipped.join('\n')}`,
            color: PRIMARY_BLUE,
          }),
        );
      }

      // update state
      if (result.success.length > 0) {
        dispatch(
          setHighPriorityValue({ po_numbers: result.success, value: true }),
        );
      }
    }, dispatch);
  },
);

export const unPrioritizeOrderPick = createAsyncThunk(
  'dashboard/unPrioritizeOrderPick',
  async (payload: { poNumber: string; location?: number }, { dispatch }) => {
    defaultActionProcess(async () => {
      const { poNumber, location } = payload;
      const success = await swiperxWmsApiDashboard.unPrioritizeOrderPick(
        poNumber,
        location,
      );

      if (success) {
        dispatch(
          setHighPriorityValue({
            po_numbers: [poNumber],
            value: false,
          }),
        );
        dispatch(
          snackbarSetData({
            open: true,
            message: `Unprioritize PO ${poNumber} success!`,
            color: PRIMARY_GREEN,
          }),
        );
      } else {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Unprioritize PO ${poNumber} cannot be unprioritized`,
            color: PRIMARY_BLUE,
          }),
        );
      }
    }, dispatch);
  },
);

export const bulkUnPrioritizeOrderPick = createAsyncThunk(
  'dashboard/bulkUnPrioritizeOrderPick',
  async (payload: { poNumbers: string[]; location?: number }, { dispatch }) => {
    defaultActionProcess(async () => {
      const { poNumbers, location } = payload;
      const result = await swiperxWmsApiDashboard.bulkUnPrioritizeOrderPick(
        poNumbers,
        location,
      );

      if (result.skipped.length === 0) {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Unprioritize PO success! \n ${result.success.join('\n')}`,
            color: PRIMARY_GREEN,
          }),
        );
      } else {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Unprioritize PO success: \n ${result.success.join(
              '\n',
            )} \n while PO skipped: \n ${result.skipped.join('\n')}`,
            color: PRIMARY_BLUE,
          }),
        );
      }

      // update state
      if (result.success.length > 0) {
        dispatch(
          setHighPriorityValue({ po_numbers: result.success, value: false }),
        );
      }
    }, dispatch);
  },
);

export const retryUpdateFailedOrder = createAsyncThunk(
  'dashboard/retryUpdateFailedOrder',
  async (payload: { poNumber: string; location?: number }, { dispatch }) => {
    const { poNumber, location } = payload;
    defaultActionProcess(async () => {
      const success = await swiperxWmsApiDashboard.retryUpdate(
        poNumber,
        location,
      );

      if (success) {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Submit retry PO ${poNumber} done!`,
            color: PRIMARY_GREEN,
          }),
        );
      } else {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Submit retry PO ${poNumber} failed!`,
            color: PRIMARY_BLUE,
          }),
        );
      }
    }, dispatch);
  },
);

export const bulkRetryUpdateFailedOrder = createAsyncThunk(
  'dashboard/bulkRetryUpdateFailedOrder',
  async (payload: { poNumbers: string[]; location?: number }, { dispatch }) => {
    defaultActionProcess(async () => {
      const { poNumbers, location } = payload;
      const result = await swiperxWmsApiDashboard.bulkRetryUpdate(
        poNumbers,
        location,
      );

      if (result.skipped.length === 0) {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Submit Retry PO done! \n ${result.success.join('\n')}`,
            color: PRIMARY_GREEN,
          }),
        );
      } else {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Submit Retry PO done: \n ${result.success.join(
              '\n',
            )} \n while PO skipped: \n ${result.skipped.join('\n')}`,
            color: PRIMARY_BLUE,
          }),
        );
      }
    }, dispatch);
  },
);

export const getWMSActiveLocations = createAsyncThunk(
  'dashboard/getWMSActiveLocations',
  async (userWarehouseId: number, { dispatch }) => {
    defaultActionProcess(async () => {
      const data = await swiperxWmsApiDashboard.getActiveLocations();
      if (data) {
        dispatch(setActiveLocations(data));
        const userLocation = data.find(
          (loc) => loc.warehouse_id === userWarehouseId,
        );
        dispatch(setSelectedLocation(userLocation ?? data[0]));
      }
    }, dispatch);
  },
);

export const prioritizeReplenishTask = createAsyncThunk(
  'dashboard/prioritizeReplenishTask',
  async (
    payload: { replenishNumber: string; location?: number },
    { dispatch },
  ) => {
    defaultActionProcess(async () => {
      const { replenishNumber, location } = payload;
      const success = await swiperxWmsApiDashboard.prioritizeReplenishTask(
        replenishNumber,
        location,
      );

      if (success) {
        dispatch(
          setHighPriorityValueReplenish({
            replenish_numbers: [replenishNumber],
            value: true,
          }),
        );
        dispatch(
          snackbarSetData({
            open: true,
            message: `Prioritize Replenish ${replenishNumber} success!`,
            color: PRIMARY_GREEN,
          }),
        );
      } else {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Prioritize Replenish ${replenishNumber} cannot be prioritized`,
            color: PRIMARY_BLUE,
          }),
        );
      }
    }, dispatch);
  },
);

export const bulkPrioritizeReplenishTask = createAsyncThunk(
  'dashboard/bulkPrioritizeReplenishTask',
  async (
    payload: { replenishNumbers: string[]; location?: number },
    { dispatch },
  ) => {
    defaultActionProcess(async () => {
      const { replenishNumbers, location } = payload;
      const result = await swiperxWmsApiDashboard.bulkPrioritizeReplenishTask(
        replenishNumbers,
        location,
      );

      if (result.skipped.length === 0) {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Prioritize Replenish success!
                      \n ${result.success.join('\n')}`,
            color: PRIMARY_GREEN,
          }),
        );
      } else {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Prioritize Replenish success: \n ${result.success.join(
              '\n',
            )} \n while Replenish skipped: \n ${result.skipped.join('\n')}`,
            color: PRIMARY_BLUE,
          }),
        );
      }

      // update state
      if (result.success.length > 0) {
        dispatch(
          setHighPriorityValueReplenish({
            replenish_numbers: result.success,
            value: true,
          }),
        );
      }
    }, dispatch);
  },
);

export const unPrioritizeReplenishTask = createAsyncThunk(
  'dashboard/unPrioritizeReplenishTask',
  async (
    payload: { replenishNumber: string; location?: number },
    { dispatch },
  ) => {
    defaultActionProcess(async () => {
      const { replenishNumber, location } = payload;
      const success = await swiperxWmsApiDashboard.unPrioritizeReplenishTask(
        replenishNumber,
        location,
      );

      if (success) {
        dispatch(
          setHighPriorityValueReplenish({
            replenish_numbers: [replenishNumber],
            value: false,
          }),
        );
        dispatch(
          snackbarSetData({
            open: true,
            message: `Unprioritize Replenish ${replenishNumber} success!`,
            color: PRIMARY_GREEN,
          }),
        );
      } else {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Replenish ${replenishNumber} cannot be unprioritized`,
            color: PRIMARY_BLUE,
          }),
        );
      }
    }, dispatch);
  },
);

export const bulkUnPrioritizeReplenishTask = createAsyncThunk(
  'dashboard/bulkUnPrioritizeReplenishTask',
  async (
    payload: { replenishNumbers: string[]; location?: number },
    { dispatch },
  ) => {
    defaultActionProcess(async () => {
      const { replenishNumbers, location } = payload;
      const result = await swiperxWmsApiDashboard.bulkUnPrioritizeReplenishTask(
        replenishNumbers,
        location,
      );

      if (result.skipped.length === 0) {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Unprioritize Replenish success!
                      \n ${result.success.join('\n')}`,
            color: PRIMARY_GREEN,
          }),
        );
      } else {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Unprioritize Replenish success: \n ${result.success.join(
              '\n',
            )} \n while Replenish skipped: \n ${result.skipped.join('\n')}`,
            color: PRIMARY_BLUE,
          }),
        );
      }

      // update state
      if (result.success.length > 0) {
        dispatch(
          setHighPriorityValueReplenish({
            replenish_numbers: result.success,
            value: false,
          }),
        );
      }
    }, dispatch);
  },
);

export const retryUpdateFailedOrderReplenish = createAsyncThunk(
  'dashboard/retryUpdateFailedOrderReplenish',
  async (
    payload: { replenishNumber: string; location?: number },
    { dispatch },
  ) => {
    const { replenishNumber, location } = payload;
    defaultActionProcess(async () => {
      const success = await swiperxWmsApiDashboard.retryUpdateReplenish(
        replenishNumber,
        location,
      );

      if (success) {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Submit retry Replenish Task ${replenishNumber} done!`,
            color: PRIMARY_GREEN,
          }),
        );
      }
    }, dispatch);
  },
);

export const bulkRetryUpdateFailedReplenish = createAsyncThunk(
  'dashboard/bulkRetryUpdateFailedReplenish',
  async (
    payload: { replenishNumbers: string[]; location?: number },
    { dispatch },
  ) => {
    defaultActionProcess(async () => {
      const { replenishNumbers, location } = payload;
      const result = await swiperxWmsApiDashboard.bulkRetryUpdateReplenish(
        replenishNumbers,
        location,
      );

      if (result.skipped.length === 0) {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Submit Retry Replenish Task done!
            \n ${result.success.join('\n')}`,
            color: PRIMARY_GREEN,
          }),
        );
      } else {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Submit Retry Replenish Task done: \n ${result.success.join(
              '\n',
            )} \n while Replenish Task skipped:
            \n ${result.skipped.join('\n')}`,
            color: PRIMARY_BLUE,
          }),
        );
      }
    }, dispatch);
  },
);

export const removeReplenishQueue = createAsyncThunk(
  'dashboard/removeReplenishQueue',
  async (
    payload: { replenishType: ReplenishType; location?: number },
    { dispatch },
  ) => {
    const { replenishType, location } = payload;
    defaultActionProcess(async () => {
      const result = await swiperxWmsApiDashboard.removeQueue(
        replenishType,
        location,
      );

      if (result) {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Queue successfully removed: ${result.deletedCount} task(s)!`,
            color: PRIMARY_BLUE,
          }),
        );
        if (result.deletedCount > 0) {
          dispatch(setUpdateListPage());
        }
      }
    }, dispatch);
  },
);

export const bulkRetryUpdateFailedStockTransfer = createAsyncThunk(
  'dashboard/bulkRetryUpdateFailedStockTransfer',
  async (
    payload: { transferNumbers: string[]; location?: number },
    { dispatch },
  ) => {
    defaultActionProcess(async () => {
      const { transferNumbers, location } = payload;
      const result = await swiperxWmsApiDashboard.retryUpdateStockTransfer(
        transferNumbers,
        location,
      );

      if (result.skipped.length === 0) {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Submit Retry Bin Transfer done!
            \n ${result.success.join('\n')}`,
            color: PRIMARY_GREEN,
          }),
        );
      } else {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Submit Retry Bin Transfer done: \n ${result.success.join(
              '\n',
            )} \n while Bin Transfer skipped:
            \n ${result.skipped.join('\n')}`,
            color: PRIMARY_BLUE,
          }),
        );
      }
    }, dispatch);
  },
);

export const bulkCancelStockTransfer = createAsyncThunk(
  'dashboard/bulkCancelStockTransfer',
  async (
    payload: { transferNumbers: string[]; location?: number },
    { dispatch },
  ) => {
    return defaultActionProcess(async () => {
      const { transferNumbers, location } = payload;
      const result = await swiperxWmsApiDashboard.bulkCancelStockTransfer(
        transferNumbers,
        location,
      );

      if (result.skipped.length === 0) {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Cancel Bin Transfer done!\n ${result.success.join('\n')}`,
            color: PRIMARY_GREEN,
          }),
        );
      } else {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Cancel Bin Transfer done: \n ${result.success.join(
              '\n',
            )} \n while Bin Transfer skipped:\n ${result.skipped.join('\n')}`,
            color: PRIMARY_BLUE,
          }),
        );
      }
    }, dispatch);
  },
);

export const bulkUpload = createAsyncThunk(
  'dashboard/bulkUpload',
  async (
    payload: { file: File; fileImportType: FileImportType; location?: number },
    { dispatch },
  ) => {
    defaultActionProcess(async () => {
      const { file, fileImportType, location } = payload;
      const result = await swiperxWmsApiDashboard.bulkUpload(
        file,
        fileImportType,
        location,
      );

      switch (result.status) {
        case CSVUploadStatus.Warning:
          dispatch(
            snackbarSetData({
              open: true,
              message: `"${result.file_name}" uploaded with warning: ${result.invalid_rows} invalid rows`,
              color: PRIMARY_BLUE,
            }),
          );
          return;
        case CSVUploadStatus.Processing:
          dispatch(
            snackbarSetData({
              open: true,
              message: `"${result.file_name}" is queued for processing`,
              color: PRIMARY_BLUE,
            }),
          );
          return;
        default:
          dispatch(
            snackbarSetData({
              open: true,
              message: `"${result.file_name}" uploaded successfully`,
              color: PRIMARY_GREEN,
            }),
          );
      }
    }, dispatch);
  },
);

export const getBulkUploadHistoryAction = createAsyncThunk(
  'dashboard/getProductUploadHistoryAction',
  async (
    params: { searchParams?: URLSearchParams; locationId?: string },
    { dispatch },
  ) => {
    const { searchParams, locationId } = params;
    const queries: Record<string, string | string> = {};
    searchParams?.forEach((value, key) => {
      queries[key] = value;
    });

    if (locationId) queries.location = locationId;

    defaultActionProcess(async () => {
      const data =
        await swiperxWmsApiDashboard.getBulkUploadHistoryPaginated(queries);
      dispatch(setBulkUploadHistory(data));
    }, dispatch);
  },
);

export const completePackingDashboard = createAsyncThunk(
  'dashboard/completePacking',
  async (payload: { poNumber: string; location?: number }, { dispatch }) => {
    const { poNumber, location } = payload;
    defaultActionProcess(async () => {
      await swiperxWmsApiDashboard.completePacking(poNumber, location);
    }, dispatch);
    dispatch(
      snackbarSetData({
        open: true,
        message: `Successfully updated PO ${poNumber} to packed`,
        color: PRIMARY_GREEN,
      }),
    );
    dispatch(setUpdateListPage());
  },
);

export const postCreateBin = createAsyncThunk(
  'dashboard/postCreateBin',
  async (
    params: {
      bin: Pick<ProductDetail, 'bin' | 'bin_type' | 'commodity'>;
      location?: number;
    },
    { dispatch },
  ) => {
    try {
      const { bin, location } = params;
      defaultActionProcess(async () => {
        await swiperxWmsApiDashboard.postCreateBin(bin, location);
        dispatch(
          addBinToList({
            _id: '',
            name: bin.bin,
            bin_type: bin.bin_type,
            commodity: bin.commodity,
            warehouse_id: location ?? 0,
            is_active: true,
            volume: 0,
            netsuite_sync_status: BinNetsuiteSyncStatusEnum.UPDATING,
          }),
        );
        dispatch(
          snackbarSetData({
            open: true,
            message: `Successfully created bin ${bin.bin}`,
            color: PRIMARY_GREEN,
          }),
        );
      }, dispatch);
    } catch (error: any) {
      dispatch(
        snackbarSetData({
          open: true,
          message: error.message,
          color: PRIMARY_RED,
        }),
      );
    }
  },
);

export const retryUpdateFailedInventoryAdjustment = createAsyncThunk(
  'dashboard/retryUpdateFailedInventoryAdjustment',
  async (
    payload: { documentNumber: string; location?: number },
    { dispatch },
  ) => {
    const { documentNumber, location } = payload;
    defaultActionProcess(async () => {
      const success =
        await swiperxWmsApiDashboard.retryUpdateInventoryAdjustment(
          documentNumber,
          location,
        );

      if (success) {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Submit retry Inventory Adjustment ${documentNumber} done!`,
            color: PRIMARY_GREEN,
          }),
        );
      } else {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Submit retry inventory adjustment ${documentNumber} failed!`,
            color: PRIMARY_BLUE,
          }),
        );
      }
    }, dispatch);
  },
);

export const bulkRetryUpdateInventoryAdjustment = createAsyncThunk(
  'dashboard/bulkRetryUpdateInventoryAdjustment',
  async (
    payload: { documentNumbers: string[]; location?: number },
    { dispatch },
  ) => {
    defaultActionProcess(async () => {
      const { documentNumbers, location } = payload;
      const result = await swiperxWmsApiDashboard.bulkRetryUpdate(
        documentNumbers,
        location,
      );

      if (result.skipped.length === 0) {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Submit Retry Inventory Adjustment done: \n ${result.success.join(
              '\n',
            )} \n while Inventory Adjustment skipped:
              \n,
              ${result.skipped.join('\n')}`,
            color: PRIMARY_BLUE,
          }),
        );
      }
    }, dispatch);
  },
);
export const setCancelNonPickedTask = createAsyncThunk(
  'dashboard/setCancelNonPickedTask',
  async (payload: { poNumber: string; location?: number }, { dispatch }) => {
    defaultActionProcess(async () => {
      const { poNumber, location } = payload;
      const success = await swiperxWmsApiDashboard.setCancelNonPickedTask(
        poNumber,
        location,
      );

      if (success) {
        dispatch(
          removeOrderFromList({
            po_numbers: [poNumber],
          }),
        );
        dispatch(
          snackbarSetData({
            open: true,
            message: `Cancel PO ${poNumber} success!`,
            color: PRIMARY_GREEN,
          }),
        );
      } else {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Cancel PO ${poNumber} failed.`,
            color: PRIMARY_BLUE,
          }),
        );
      }
    }, dispatch);
  },
);

export const updateBinQuantity = createAsyncThunk(
  'dashboard/patchUpdateBinQuantity',
  async (
    params: {
      bin: Pick<
        ProductDetail,
        'bin' | 'sku_code' | 'inventory_number' | 'bin_type' | 'quantity'
      >;
      location?: number;
    },
    { dispatch },
  ) => {
    try {
      const { bin, location } = params;
      defaultActionProcess(async () => {
        await swiperxWmsApiDashboard.updateBinQuantity({
          sku_code: bin.sku_code,
          bin: bin.bin,
          bin_type: bin.bin_type,
          inventory_number: bin.inventory_number.trim(),
          quantity: bin.quantity,
          location,
        });
        dispatch(
          snackbarSetData({
            open: true,
            message: `Successfully updated quantity for bin ${bin.bin}`,
            color: PRIMARY_GREEN,
          }),
        );
      }, dispatch);
    } catch (error: any) {
      dispatch(
        snackbarSetData({
          open: true,
          message: error.message,
          color: PRIMARY_RED,
        }),
      );
    }
  },
);

export const updateBinInventoryNumber = createAsyncThunk(
  'dashboard/patchUpdateBinInventoryNumber',
  async (
    params: {
      bin: Pick<
        ProductDetail,
        'bin' | 'sku_code' | 'inventory_number' | 'bin_type'
      >;
      newInventoryNumber: string;
      location?: number;
    },
    { dispatch },
  ) => {
    try {
      const { bin, newInventoryNumber, location } = params;
      defaultActionProcess(async () => {
        await swiperxWmsApiDashboard.updateBinInventoryNumber({
          sku_code: bin.sku_code,
          bin: bin.bin,
          bin_type: bin.bin_type,
          inventory_number: bin.inventory_number.trim(),
          new_inventory_number: newInventoryNumber.trim(),
          location,
        });
        dispatch(
          snackbarSetData({
            open: true,
            message: `Successfully updated inventory number for bin ${bin.bin}`,
            color: PRIMARY_GREEN,
          }),
        );
      }, dispatch);
    } catch (error: any) {
      dispatch(
        snackbarSetData({
          open: true,
          message: error.message,
          color: PRIMARY_RED,
        }),
      );
    }
  },
);

export const updateBinType = createAsyncThunk(
  'dashboard/patchUpdateBinType',
  async (
    params: {
      bin: string;
      newBinType: string;
      location?: number;
    },
    { dispatch },
  ) => {
    try {
      const { bin, newBinType, location } = params;
      defaultActionProcess(async () => {
        await swiperxWmsApiDashboard.updateBinType({
          bin,
          new_bin_type: newBinType.trim(),
          location,
        });
        dispatch(setBinType({ bin, bin_type: newBinType }));
        dispatch(
          snackbarSetData({
            open: true,
            message: `Successfully updated type for bin ${bin}`,
            color: PRIMARY_GREEN,
          }),
        );
      }, dispatch);
    } catch (error: any) {
      dispatch(
        snackbarSetData({
          open: true,
          message: error.message,
          color: PRIMARY_RED,
        }),
      );
    }
  },
);

export const updateBinCommodity = createAsyncThunk(
  'dashboard/patchUpdateBinCommodity',
  async (
    params: {
      bin: string;
      newCommodity: string | null;
      location?: number;
    },
    { dispatch },
  ) => {
    try {
      const { bin, newCommodity, location } = params;
      defaultActionProcess(async () => {
        await swiperxWmsApiDashboard.updateBinCommodity({
          bin,
          new_commodity: newCommodity,
          location,
        });
        dispatch(setBinCommodity({ bin, commodity: newCommodity }));
        dispatch(
          snackbarSetData({
            open: true,
            message: `Successfully updated commodity for bin ${bin}`,
            color: PRIMARY_GREEN,
          }),
        );
      }, dispatch);
    } catch (error: any) {
      dispatch(
        snackbarSetData({
          open: true,
          message: error.message,
          color: PRIMARY_RED,
        }),
      );
    }
  },
);

export const retryCreateBinAction = createAsyncThunk(
  'dashboard/retryCreateBinAction',
  async (payload: { bin: string; location?: number }, { dispatch }) => {
    const { bin, location } = payload;
    defaultActionProcess(async () => {
      const success = await swiperxWmsApiDashboard.retryCreateBin({
        bin,
        location,
      });

      if (success) {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Submit retry NetSuite sync for bin ${bin} done!`,
            color: PRIMARY_GREEN,
          }),
        );
      } else {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Submit retry NetSuite sync for bin ${bin} failed!`,
            color: PRIMARY_BLUE,
          }),
        );
      }
    }, dispatch);
  },
);

export const getBinProductDetail = async (
  params: IBinProductDetailRequest,
  dispatch: AppDispatch,
): Promise<IBinProductDetailResponse | null> => {
  const response = await swiperxWmsApiDashboard.getBinProductDetail(params);
  if (response && response.data) {
    return response.data;
  }
  if (response.error) {
    const errorMessage =
      response.error === true
        ? 'Failed to get product detail. Please make sure Inventory Number is correct.'
        : response.error;
    dispatch(
      snackbarSetData({
        open: true,
        message: errorMessage,
        color: PRIMARY_RED,
      }),
    );
  }
  return null;
};

export const setSTODocumentToStatusArrived = createAsyncThunk(
  'dashboard/setSTODocumentToStatusArrived',
  async (payload: { stoNumber: string; location?: number }, { dispatch }) => {
    const { stoNumber, location } = payload;
    defaultActionProcess(async () => {
      await swiperxWmsApiDashboard.setSTODocumentArrived(stoNumber, location);
    }, dispatch);
    dispatch(
      snackbarSetData({
        open: true,
        message: `Successfully updated STO ${stoNumber} to arrived`,
        color: PRIMARY_GREEN,
      }),
    );
    dispatch(setUpdateListPage());
  },
);

export const bulkSetSTODocumentToStatusArrived = createAsyncThunk(
  'dashboard/bulkSetSTODocumentToStatusArrived',
  async (
    payload: { stoNumbers: string[]; location?: number },
    { dispatch },
  ) => {
    const { stoNumbers, location } = payload;
    defaultActionProcess(async () => {
      await swiperxWmsApiDashboard.bulkSetSTODocumentArrived(
        stoNumbers,
        location,
      );
    }, dispatch);
    dispatch(
      snackbarSetData({
        open: true,
        message: `Successfully updated STO ${stoNumbers.join(', ')} to arrived`,
        color: PRIMARY_GREEN,
      }),
    );
    dispatch(setUpdateListPage());
  },
);

export const getDeliveryNoteListAction = createAsyncThunk(
  'dashboard/getDeliveryNoteListAction',
  async (
    params: { searchParams?: URLSearchParams; locationId?: string },
    { dispatch },
  ) => {
    const { searchParams, locationId } = params;

    const hasOtherParams =
      searchParams &&
      Array.from(searchParams.keys()).some(
        (key) => !defaultSearchParams.has(key),
      );

    if (hasOtherParams) {
      const queries: Record<string, string> = {};
      searchParams?.forEach((value, key) => {
        if (key !== 'status' || value !== DeliveryNoteSearchCriteriaEnum.ALL)
          queries[key] = value;
      });

      if (locationId) queries.location = locationId;

      defaultActionProcess(async () => {
        const data =
          await swiperxWmsApiDashboard.getDeliveryNoteListPaginated(queries);
        dispatch(setAllDeliveryNoteLists(data));
      }, dispatch);
    }
  },
);

export const syncAwbAction = createAsyncThunk(
  'dashboard/syncAwbAction',
  async (payload: { poNumbers: string[] }, { dispatch }) => {
    defaultActionProcess(async () => {
      const { poNumbers } = payload;
      await swiperxWmsApiDashboard.syncAwb(poNumbers);
      dispatch(
        snackbarSetData({
          open: true,
          message: `Sync AWB Info for PO(s) ${poNumbers.join(', ')} done!`,
          color: PRIMARY_GREEN,
        }),
      );
    }, dispatch);
  },
);

export const cancelDeliveryNoteAction = createAsyncThunk(
  'dashboard/cancelDeliveryNoteAction',
  async (awbNumber: string, { dispatch }) => {
    return defaultActionProcess(async () => {
      await swiperxWmsApiDashboard.cancelDeliveryNote(awbNumber);
      dispatch(
        setDeliveryNoteStatus({
          awbNumber,
          status: DeliveryNoteStatusEnum.CANCELLED,
        }),
      );
      dispatch(
        snackbarSetData({
          open: true,
          message: `Delivery note ${awbNumber} cancelled!`,
          color: PRIMARY_GREEN,
        }),
      );
    }, dispatch);
  },
);

export const cancelDeliveryNoteBulkAction = createAsyncThunk(
  'dashboard/cancelDeliveryNoteBulkAction',
  async (awbNumbers: string[], { dispatch }) => {
    return defaultActionProcess(async () => {
      const { cancelled, skipped } =
        await swiperxWmsApiDashboard.cancelDeliveryNotesBulk(awbNumbers);

      cancelled.forEach((deliveryNote) => {
        dispatch(
          setDeliveryNoteStatus({
            awbNumber: deliveryNote.awb_number,
            status: DeliveryNoteStatusEnum.CANCELLED,
          }),
        );
      });

      const messageParts: string[] = [];
      if (cancelled.length) {
        messageParts.push(
          `Cancelled: ${cancelled.map((note) => note.awb_number).join(', ')}`,
        );
      }
      if (skipped.length) {
        messageParts.push(`Skipped: ${skipped.join(', ')}`);
      }

      let color = PRIMARY_BLUE;
      if (skipped.length === 0) color = PRIMARY_GREEN;
      else if (cancelled.length === 0) color = PRIMARY_RED;

      dispatch(
        snackbarSetData({
          open: true,
          message: messageParts.join(' | '),
          color,
        }),
      );
    }, dispatch);
  },
);

export const retryUpdateFailedSTODocuments = createAsyncThunk(
  'dashboard/retryUpdateFailedSTODocuments',
  async (
    payload: { stoNumbers: string[]; location?: number },
    { dispatch },
  ) => {
    const { stoNumbers, location } = payload;
    defaultActionProcess(async () => {
      const success = await swiperxWmsApiDashboard.retryUpdateFailedSTO(
        stoNumbers,
        location,
      );

      if (success) {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Submit retry Replenish Task ${stoNumbers.join(
              ', ',
            )} done!`,
            color: PRIMARY_GREEN,
          }),
        );
      }
    }, dispatch);
  },
);
